import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { Ban, CheckCircle2, History, Mail } from 'lucide-react';
import { ConfirmationModal } from '@/components/modal';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PortalAccessLogList } from '@/components/matter/sharing/PortalAccessLogList';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import matterSharingService from '@/services/portal/matterSharingService';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const STATUS = {
  invited: { label: 'Invite pending', badge: 'badge-warning' },
  active: { label: 'Active', badge: 'badge-success' },
  disabled: { label: 'Disabled', badge: 'badge-danger' },
};

/**
 * Everyone the firm has shared matter files with (OL-194). Shares are counted,
 * not listed: the matter names live on each matter's own Sharing panel, where
 * matter access is checked.
 */
const ClientPortalGuests = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [disabling, setDisabling] = useState(null);

  const load = useCallback(async () => {
    try {
      setGuests(await matterSharingService.getGuests());
    } catch (error) {
      toast.error('Could not load guests.', { description: getApiErrorMessage(error, 'Please try again.') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (guest, action, success) => {
    setBusyId(guest.id);
    try {
      await action(guest.id);
      toast.success(success);
      await load();
    } catch (error) {
      toast.error('That did not work.', { description: getApiErrorMessage(error, 'Please try again.') });
    } finally {
      setBusyId(null);
    }
  };

  // One guest's activity (OL-196), in a dialog.
  const [activityFor, setActivityFor] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const openActivity = async (guest) => {
    setActivityFor(guest);
    setActivity([]);
    setActivityLoading(true);
    try {
      setActivity(await matterSharingService.getGuestAccessLog(guest.id));
    } catch (error) {
      toast.error('Could not load activity.', { description: getApiErrorMessage(error, 'Please try again.') });
    } finally {
      setActivityLoading(false);
    }
  };

  const confirmDisable = () => {
    const guest = disabling;
    setDisabling(null);
    run(guest, matterSharingService.disableGuest, `${guest.name} is disabled and signed out.`);
  };

  return (
    <div className="container-fixed">
      <div className="card">
        <div className="card-header flex-wrap gap-2">
          <div className="flex flex-col">
            <h3 className="card-title">Client Portal Guests</h3>
            <span className="text-2xs text-gray-500">
              People outside the firm who can sign in to see matter files shared with them. Share files from a matter&apos;s Files tab.
            </span>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <p className="text-center text-2sm text-gray-600 py-6">Loading…</p>
          ) : guests.length === 0 ? (
            <p className="text-center text-2sm text-gray-500 py-6">No guests yet. Share a file from a matter to invite one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-border text-2sm">
                <thead>
                  <tr>
                    <th>Guest</th>
                    <th>Status</th>
                    <th>Invited</th>
                    <th>Last sign-in</th>
                    <th>Active shares</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {guests.map((guest) => (
                    <tr key={guest.id}>
                      <td>
                        <div className="flex flex-col">
                          <span className="text-gray-900 font-medium">{guest.name}</span>
                          <span className="text-2xs text-gray-500">{guest.email}</span>
                        </div>
                      </td>
                      <td>
                        <span className={clsx('badge badge-sm badge-outline', STATUS[guest.status]?.badge)}>
                          {STATUS[guest.status]?.label ?? guest.status}
                        </span>
                      </td>
                      <td>{fmtDate(guest.invited_at) ?? '—'}</td>
                      <td>{fmtDate(guest.last_login_at) ?? 'Never'}</td>
                      <td>{guest.active_shares_count}</td>
                      <td className="text-right whitespace-nowrap">
                        <div className="flex justify-end gap-2">
                          <button className="btn btn-xs btn-light" onClick={() => openActivity(guest)}>
                            <History size={13} /> Activity
                          </button>
                          {guest.status === 'invited' && (
                            <button
                              className="btn btn-xs btn-light"
                              disabled={busyId === guest.id}
                              onClick={() => run(guest, matterSharingService.resendInvite, `A new invitation was emailed to ${guest.email}.`)}
                            >
                              <Mail size={13} /> Resend invite
                            </button>
                          )}
                          {guest.status === 'disabled' ? (
                            <button
                              className="btn btn-xs btn-light"
                              disabled={busyId === guest.id}
                              onClick={() => run(guest, matterSharingService.enableGuest, `${guest.name} can sign in again.`)}
                            >
                              <CheckCircle2 size={13} /> Enable
                            </button>
                          ) : (
                            <button
                              className="btn btn-xs btn-light text-danger"
                              disabled={busyId === guest.id}
                              onClick={() => setDisabling(guest)}
                            >
                              <Ban size={13} /> Disable
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={Boolean(activityFor)} onOpenChange={(open) => !open && setActivityFor(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Activity — {activityFor?.name}</DialogTitle>
          </DialogHeader>
          <DialogBody className="!shadow-none !border-0">
            <p className="text-2xs text-gray-500 mb-3">
              Sign-ins, documents opened and downloaded, and changes to this guest&apos;s access. Matters you can&apos;t open are left out.
            </p>
            <PortalAccessLogList rows={activity} loading={activityLoading} showMatter />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        show={Boolean(disabling)}
        onClose={() => setDisabling(null)}
        onConfirm={confirmDisable}
        title="Disable this guest?"
        btnText="Disable"
        message={`${disabling?.name} will be signed out at once and can't sign in again until you enable them. Their shares are kept.`}
      />
    </div>
  );
};

export { ClientPortalGuests };
